import React, { useState, useEffect, useCallback, useRef } from "react";
import { TabContent, TabPane } from "reactstrap";
import { useDispatch, connect } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import { Oval as Loader } from "react-loader-spinner";
import { useParams, useLocation, useHistory } from "react-router-dom";

// Assets and styles
import "./profilePage.css";

// Core components
import { useNavigation } from "../../../../shared/utils/useNavigation";
import { isUserLoggedIn, getUserRole, getUserId, isUserAdmin } from "../../../../shared/utils/function";
import NoContent from "../../../component/NoContent/NoContent";
import ModernHeader from "../../../../components/layout/Headers/ModernHeader";
import ModernFootermenu from "../../../../components/layout/Footer";

// Actions and utilities
import { TAB_TYPE, ROLE, PURCHASE_STATE } from "../../../../shared/constants/common";
import { fetchCreatorByUsername } from "../../../../shared/actions/creatorActions";
import {
  fetchFeedOfCreator,
  fetchContentByCreator,
  fetchContentCounts,
  fetchCreatorContentById
} from "../../../../shared/actions/contentAction";

// Sections and Modals
import UserProfileHeader from "../../../component/profile/header";
import FeedPost from "../../../../features/cards/user/feed";
import MediaGrid from "../../../../features/cards/user/mediaGrid";
import LockedMonthlyContentPreview from "../../../../features/cards/user/LockedMonthlyContentPreview";
import PPVFollowModal from "../../../Modals/PPVFollowModal";
import RecommendedCreators from "../../../component/Recommended/Creators";
import { calculateDaysRemaining } from "../../../../shared/utils/function";
import { resolveVatRate } from "../../../../shared/utils/paymentUtil";
import { reportError } from "../../../../shared/sentry";

const ViewCreatorProfile = ({
  user,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const history = useHistory();
  const location = useLocation();
  const { creatorUsername } = useParams();
  const isLoggedIn = isUserLoggedIn();
  const userRole = getUserRole();
  const userId = getUserId();
  const isAdmin = isUserAdmin();

  // State hooks
  const [activeTab, setActiveTab] = useState(TAB_TYPE.FEED);
  const [type, setType] = useState(TAB_TYPE.FEED);

  const [creatorLoading, setCreatorLoading] = useState(false);
  const [contentCreatorId, setContentCreatorId] = useState(null);
  const [creator, setCreator] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilterState] = useState("");
  const [contentCounts, setContentCounts] = useState({});
  const [feedLists, setFeedLists] = useState([]);
  const [mediaLists, setMediaLists] = useState([]);
  const [freeContentLists, setFreeContentLists] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [vatPercentage, setVatPercentage] = useState(0);
  const isPPVContent = creator?.user_data?.rol === ROLE.CREATOR_CONTENT;
  const isSubContent = creator?.user_data?.rol === ROLE.CREATOR_SUB;
  const isSubscribedSubContent = creator?.user_data?.subscribed;

  // Follow modal state
  const [isFollowModalOpen, setIsFollowModalOpen] = useState(false);
  const creatorInfo = creator?.user_data || {};
  
  // Track loading state and pagination per tab type
  const [tabState, setTabState] = useState({
    feed: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
    media: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
    free: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
  });

  // When URL has no username (e.g. Instagram/iOS opened only the domain): use ?u= if present, else redirect away
  useEffect(() => {
    const hasUsername = creatorUsername && String(creatorUsername).trim() !== "";
    if (hasUsername) return;

    const u = new URLSearchParams(location.search).get("u");
    const usernameFromQuery = u && String(u).trim();
    if (usernameFromQuery) {
      history.replace(`/${encodeURIComponent(usernameFromQuery)}`);
      return;
    }

    navigate.toAllCreators();
  }, [creatorUsername, location.search, history, navigate]);

  // Handle redirect if user is viewing their own creator profile
  useEffect(() => {
    if (user?.username === creatorUsername && (user?.rol === ROLE.CREATOR_SUB || user?.rol === ROLE.CREATOR_CONTENT)) {
      navigate.toProfile();
    }
  }, [user?.username, creatorUsername, user?.rol, navigate]);



  useEffect(() => {
    document.body.classList.add('pt-0')
  }, [])

  // Fetch creator information by creatorUsername
  useEffect(() => {
    const fetchCreatorData = async () => {
      if (!creatorUsername || creatorUsername.trim() === '') {
        setCreatorLoading(false);
        return;
      }

      setCreatorLoading(true);
      try {
        const result = await dispatch(fetchCreatorByUsername(creatorUsername, (creatorId, creatorData) => {
          if (creatorId && creatorData) {
            setContentCreatorId(creatorId);
            setCreator(creatorData);
          } else {
            // Error case - creator not found
            setContentCreatorId(null);
            setCreator(null);
          }
        }));

        // Handle error if fetch failed
        if (!result.success) {
          navigate.toAllCreators(); // redirect to all creators page if failed to fetch creator
          setContentCreatorId(null);
          setCreator(null);
        }
      } catch (error) {
        navigate.toAllCreators(); // redirect to all creators page if error
        setContentCreatorId(null);
        setCreator(null);
      } finally {
        setCreatorLoading(false);
      }
    };

    fetchCreatorData();
  }, [creatorUsername, dispatch]);

  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(user);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [user]);

  // for fetch content counts of creator only
  const fetchContentCountsData = useCallback(async (contentCreatorId) => {
    try {
      if (!contentCreatorId) {
        // If contentCreatorId is not available yet, wait for it
        return;
      }
      const result = await fetchContentCounts(contentCreatorId);
      if (result.success && result.data) {
        setContentCounts(result.data);
      } else {
        setContentCounts({});
      }
    } catch (err) {
      setContentCounts({});
    }
  }, []);

  const setitems = useCallback((res, isLoadMore = false, currentType = type) => {
    // Check if response has error or invalid structure
    if (!res.success || res.error || !res.data) {
      setLoading(false);
      setLoadingMore(false);
      setTabState(prev => ({
        ...prev,
        [currentType]: { ...prev[currentType], hasInitiallyLoaded: true, hasMoreData: false }
      }));
      return;
    }

    // Extract content - it might be directly in res.data or in res.data.content
    const newContent = Array.isArray(res.data) ? res.data : (res.data.content || []);
    // If we received fewer items than the limit, there's no more data
    const currentLimit = currentType === TAB_TYPE.FEED ? 6 : 12;
    const hasMore = newContent.length === currentLimit;

    if (currentType === TAB_TYPE.FEED) {
      if (isLoadMore) {
        setFeedLists(prev => [...prev, ...newContent]);
      } else {
        setFeedLists(newContent);
      }
    } else if (currentType === TAB_TYPE.MEDIA) {
      if (isLoadMore) {
        setMediaLists(prev => [...prev, ...newContent]);
      } else {
        setMediaLists(newContent);
      }
    } else if (currentType === TAB_TYPE.FREE) {
      if (isLoadMore) {
        setFreeContentLists(prev => [...prev, ...newContent]);
      } else {
        setFreeContentLists(newContent);
      }
    }

    setLoading(false);
    setLoadingMore(false);
    setTabState(prev => ({
      ...prev,
      [currentType]: { ...prev[currentType], hasInitiallyLoaded: true, hasMoreData: hasMore }
    }));
  }, [type]);


  // for fetch content 
  const fetchContent = useCallback((currentPage, isLoadMore = false, currentType = type) => {
    if (!contentCreatorId) {
      // Don't fetch content if creator ID is not available yet
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    // Calculate skip value: skip = page * limit
    const currentLimit = currentType === TAB_TYPE.FEED ? 6 : 12;
    const skip = currentPage * currentLimit;
    const filterValue = filter || undefined;

    // Choose API based on type: FEED uses fetchFeedOfCreator, MEDIA/FREE use fetchContentByCreator
    if (currentType === TAB_TYPE.FEED) {
      dispatch(fetchFeedOfCreator(
        contentCreatorId,
        currentType,
        currentLimit,
        currentPage,
        filterValue,
        (res) => {
          setitems(res, isLoadMore, currentType);
        }
      ));
    } else {
      // For MEDIA and FREE, use fetchContentByCreator
      dispatch(fetchContentByCreator(
        isLoggedIn,
        contentCreatorId,
        currentType,
        currentLimit,
        currentPage,
        filterValue,
        (res) => {
          setitems(res, isLoadMore, currentType);
        }
      ));
    }
  }, [dispatch, contentCreatorId, type, filter, setitems]);

  const loadInitialContent = useCallback(() => {
    const currentType = type;
    setTabState(prev => ({
      ...prev,
      [currentType]: { ...prev[currentType], page: 0, hasMoreData: true }
    }));
    fetchContent(0, false, currentType);
  }, [type, fetchContent]);

  const handleSetType = useCallback((newType) => {
    setType(newType);
    setFilterState("");
    // Set loading state immediately if tab hasn't been loaded yet
    if (!tabState[newType].hasInitiallyLoaded) {
      setLoading(true);
    }
  }, [tabState]);

  const loadMore = useCallback(() => {
    // Check if there's more data available and not currently loading
    const currentType = type;
    const currentTabState = tabState[currentType];
    if (!currentTabState.hasMoreData || loadingMore || loading) {
      return;
    }

    // Prevent loading more feed items if creator is sub content and user is not subscribed
    if (!isAdmin && (currentType === TAB_TYPE.FEED && !isSubscribedSubContent && creatorInfo.rol !== ROLE.CREATOR_CONTENT)) {
      return;
    }

    const nextPage = currentTabState.page + 1;
    setTabState(prev => ({
      ...prev,
      [currentType]: { ...prev[currentType], page: nextPage }
    }));
    fetchContent(nextPage, true, currentType);
  }, [tabState, type, loadingMore, loading, fetchContent, isSubContent, isSubscribedSubContent]);

  // Fetch content counts once creator ID is available
  useEffect(() => {
    if (contentCreatorId) {
      fetchContentCountsData(contentCreatorId);
    }
  }, [contentCreatorId, fetchContentCountsData]);

  // On page load: fetch feed content when creator info is available (initial load)
  useEffect(() => {
    if (contentCreatorId && !tabState[TAB_TYPE.FEED].hasInitiallyLoaded) {
      // Load feed content on initial page load
      setTabState(prev => ({
        ...prev,
        feed: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
      }));
      fetchContent(0, false, TAB_TYPE.FEED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentCreatorId]);

  // Handle type changes to load content - only load if not already loaded
  // Note: Initial feed load is handled by the effect above when contentCreatorId is first set
  useEffect(() => {
    // Skip if this is the FEED tab on initial load - it's handled by the effect above
    if (contentCreatorId && type && !tabState[type].hasInitiallyLoaded && type !== TAB_TYPE.FEED) {
      loadInitialContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Manage modal-open class for follow modal
  useEffect(() => {
    if (isFollowModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isFollowModalOpen]);

  // Ref for the sentinel element (load more trigger)
  const loadMoreSentinelRef = useRef(null);

  // Setup Intersection Observer for load more (more reliable across browsers)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    // Use Intersection Observer API for better cross-browser compatibility
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When sentinel element is visible (or near visible), load more
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Trigger 200px before reaching the sentinel
        threshold: 0.1 // Trigger when 10% of sentinel is visible
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore, type]); // Re-initialize when tab type changes

  const refreshContent = useCallback(() => {
    // Reset the current tab state
    const currentType = type;
    setTabState(prev => ({
      ...prev,
      [currentType]: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
    }));
    fetchContent(0, false, currentType);

    // Refresh content counts
    if (contentCreatorId) {
      fetchContentCountsData(contentCreatorId);
    }
  }, [type, fetchContent, contentCreatorId, fetchContentCountsData]);

  const refreshAfterSubscriptionSuccess = useCallback(async () => {
    if (!creatorUsername) return;

    try {
      const result = await dispatch(fetchCreatorByUsername(creatorUsername, (creatorId, creatorData) => {
        if (creatorId && creatorData) {
          setContentCreatorId(creatorId);
          setCreator(creatorData);
        }
      }));

      setTabState({
        feed: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
        media: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
        free: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
      });
      setActiveTab(TAB_TYPE.FEED);
      setType(TAB_TYPE.FEED);

      const resolvedCreatorId =
        result?.data?._id ||
        result?.data?.id ||
        result?.data?.user_data?._id ||
        contentCreatorId;

      if (resolvedCreatorId) {
        fetchContentCountsData(resolvedCreatorId);
        setLoading(true);
        fetchContent(0, false, TAB_TYPE.FEED);
      }
    } catch (error) {
      reportError(error, 'ViewCreatorProfile refresh after subscription');
    }
  }, [creatorUsername, dispatch, contentCreatorId, fetchContentCountsData, fetchContent]);

  // Handle dropdown toggle - only one dropdown open at a time
  const handleDropdownToggle = useCallback((postId) => {
    setOpenDropdownId(prev => prev === postId ? null : postId);
  }, []);

  const toggleFollowModal = useCallback(() => {
    setIsFollowModalOpen(prev => !prev);
  }, []);

  // after refresh purchased content or tip pay
  const onPurchaseSuccess = useCallback(async (purchasedPost) => {
    if (!purchasedPost?._id || !contentCreatorId) {
      return;
    }

    try {
      const response = await fetchCreatorContentById(contentCreatorId, purchasedPost._id);

      if (!response.success || !response.data) {
        return;
      }

      const extractPostFromResponse = (data) => {
        if (!data) {
          return null;
        }

        if (Array.isArray(data)) {
          return data[0] || null;
        }

        if (data.content && typeof data.content === 'object') {
          return data.content;
        }

        if (data.data && typeof data.data === 'object') {
          return data.data;
        }

        if (data.post) {
          return data.post;
        }

        return data;
      };

      const updatedPost = extractPostFromResponse(response.data);
      if (!updatedPost) {
        return;
      }

      const getComparableId = (post) => post?._id || post?.content_id || post?.content?._id;
      const updatedPostId = getComparableId(updatedPost);

      if (!updatedPostId) {
        return;
      }

      const mergePosts = (existingPost) => {
        const mergedPost = { ...existingPost, ...updatedPost };

        if (existingPost?.content || updatedPost?.content) {
          mergedPost.content = {
            ...(existingPost?.content || {}),
            ...(updatedPost?.content || {})
          };
        }

        return mergedPost;
      };

      const applyUpdateToList = (list) => {
        if (!Array.isArray(list) || list.length === 0) {
          return list;
        }

        let hasChanges = false;

        const nextList = list.map((item) => {
          const itemId = getComparableId(item);
          if (itemId === updatedPostId) {
            hasChanges = true;
            return mergePosts(item);
          }
          return item;
        });

        return hasChanges ? nextList : list;
      };

      setFeedLists((prev) => applyUpdateToList(prev));
      setMediaLists((prev) => applyUpdateToList(prev));
      setFreeContentLists((prev) => applyUpdateToList(prev));
    } catch (error) {
      reportError(error, 'ViewCreatorProfile refresh purchased content');
    }
  }, [contentCreatorId]);

  // Handle open follow modal
  const handleOpenFollowModal = useCallback(() => {
    setIsFollowModalOpen(true);
  }, []);

  return (
    <div className="profile-page-creator modern-creator-profile">
      <ModernHeader
        hide={false}
        showAvatar={false}
        showBackButton={true}
        hideSearchIcon={true}
      />

      <main className="profile-main-content">
        <UserProfileHeader
          isLogging={isLoggedIn}
          userRole={userRole}
          isOwnProfile={userId === creator?.user_data?._id}
          creatorLoading={creatorLoading}
          creatorInfo={creator?.user_data || {}}
          onSubscriptionSuccess={refreshAfterSubscriptionSuccess}
        />

        {/* Content Tabs */}
        <div className="tabs_section_min_height">
          <section className="content-tabs-section">
            <div className="content-tabs-container">
              <button
                className={`content-tab ${activeTab === TAB_TYPE.FEED ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.FEED);
                  handleSetType(TAB_TYPE.FEED);
                }}
              >
                <div className="tab-icon-grid">
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                </div>
                <div className="tab-content">
                  <span>Feed</span>
                  {contentCounts.feedCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.feedCount}
                    </span>
                  )}
                </div>
              </button>

              <button
                className={`content-tab ${activeTab === TAB_TYPE.MEDIA ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.MEDIA);
                  handleSetType(TAB_TYPE.MEDIA);
                }}
              >
                <div className="tab-icon">
                  <i className="fa-solid fa-photo-film tab-icon-fa"></i>
                </div>
                <div className="tab-content">
                  <span>Media</span>
                  {contentCounts.mediaCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.mediaCount}
                    </span>
                  )}
                </div>
              </button>

              <button
                className={`content-tab ${activeTab === TAB_TYPE.FREE ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.FREE);
                  handleSetType(TAB_TYPE.FREE);
                }}
              >
                <div className="tab-icon">
                  <i className="fa-solid fa-gift tab-icon-fa"></i>
                </div>
                <div className="tab-content">
                  <span>Free</span>
                  {contentCounts.freeContentCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.freeContentCount}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </section>
        </div>

        {/* Content Feed */}
        <section className="creator-feed-section">
          <TabContent activeTab={activeTab}>
            <TabPane className="text-center" tabId={TAB_TYPE.FEED} id="follows">
              {loading && !tabState.feed.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.feed.hasInitiallyLoaded && (!feedLists || feedLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                // if creator is sub and user is not subscribed, show LockedMonthlyContentPreview else show feed posts
                (isAdmin || isPPVContent ||
                  (isSubContent && creator?.user_data?.subscribed &&
                    (creator?.user_data?.subscription?.state === PURCHASE_STATE.ACTIVE ||
                      calculateDaysRemaining(creator?.user_data?.subscription?.expire_at) >= 0)
                  )
                ) ?
                  feedLists && feedLists.map((post, index) => (
                    <React.Fragment key={post._id || index}>
                      <FeedPost
                        postId={post._id || index}
                        post={post}
                        posts={mediaLists}
                        currentIndex={index}
                        creatorRole={creator?.user_data?.rol || null}
                        isCreatorOwnProfile={userId === creator?.user_data?._id}
                        creatorInfo={creator?.user_data || {}}
                        openDropdownId={openDropdownId}
                        onDropdownToggle={handleDropdownToggle}
                        isShowDuration={true}
                        showControls={true}
                        isPPVContent={isPPVContent}
                        isSubContent={isSubContent}
                        isShowChatButton={false}
                        isShowEllipsis={true}
                        onPurchaseSuccess={onPurchaseSuccess}
                        onOpenFollowModal={() => handleOpenFollowModal()}
                        vatPercentage={vatPercentage}
                        priority={index < 2}
                      />
                      {/* Show Recommended Creators after every 6 posts or if feedLists.length < 6 */}
                      {isLoggedIn && ((index + 1) % 6 === 0 || (index === feedLists.length - 1 && feedLists.length < 6)) && (
                        <RecommendedCreators
                          currentCreatorName={creator?.user_data?.username}
                          vatPercentage={vatPercentage}
                        />
                      )}
                    </React.Fragment>
                  ))
                  :
                  <LockedMonthlyContentPreview
                    creatorInfo={creator?.user_data || {}}
                    onSubscriptionSuccess={refreshAfterSubscriptionSuccess}
                  />
              )}
            </TabPane>
            <TabPane className="text-center" tabId={TAB_TYPE.MEDIA} id="following">
              {loading && !tabState.media.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.media.hasInitiallyLoaded && (!mediaLists || mediaLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                <section id="creator-feed" className="creator-feed-grid">
                  <div className="creator-feed-grid-container">
                    {mediaLists && mediaLists.map((post, index) => (
                      <MediaGrid
                        key={post._id || index}
                        tabType={TAB_TYPE.MEDIA}
                        postId={post._id || index}
                        index={index}
                        post={post}
                        isLogging={isLoggedIn}
                        userRole={userRole}
                        isCreatorOwnProfile={userId === creator?.user_data?._id}
                        creatorInfo={creator?.user_data || {}}
                        vatPercentage={vatPercentage}
                        openDropdownId={openDropdownId}
                        onDropdownToggle={handleDropdownToggle}
                        isShowDuration={false}
                        showControls={false}
                        hideSliderDots={true}
                        isPPVContent={isPPVContent}
                        isSubContent={isSubContent}
                        posts={mediaLists}
                        currentIndex={index}
                        onPurchaseSuccess={onPurchaseSuccess}
                        onOpenFollowModal={() => handleOpenFollowModal()}
                        onSubscriptionSuccess={refreshAfterSubscriptionSuccess}
                      />
                    ))}
                  </div>
                </section>
              )}
            </TabPane>
            <TabPane className="text-center" tabId={TAB_TYPE.FREE} id="freeContent">
              {loading && !tabState.free.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.free.hasInitiallyLoaded && (!freeContentLists || freeContentLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                <section id="creator-feed" className="creator-feed-grid">
                  <div className="creator-feed-grid-container">
                    {freeContentLists && freeContentLists.map((post, index) => (
                      <MediaGrid
                        key={post._id || index}
                        tabType={TAB_TYPE.FREE}
                        postId={post._id || index}
                        post={post}
                        index={index}
                        isLogging={isLoggedIn}
                        userRole={userRole}
                        isCreatorOwnProfile={userId === creator?.user_data?._id}
                        creatorInfo={creator?.user_data || {}}
                        vatPercentage={vatPercentage}
                        openDropdownId={openDropdownId}
                        onDropdownToggle={handleDropdownToggle}
                        isShowDuration={false}
                        showControls={false}
                        hideSliderDots={true}
                        isPPVContent={isPPVContent}
                        isSubContent={isSubContent}
                        posts={freeContentLists}
                        currentIndex={index}
                        onPurchaseSuccess={onPurchaseSuccess}
                        onOpenFollowModal={() => handleOpenFollowModal()}
                        onSubscriptionSuccess={refreshAfterSubscriptionSuccess}
                      />
                    ))}
                  </div>
                </section>
              )}
            </TabPane>
          </TabContent>

          {/* Sentinel element for Intersection Observer - triggers load more when visible */}
          {tabState[type]?.hasInitiallyLoaded && tabState[type]?.hasMoreData && !loadingMore && (
            <div ref={loadMoreSentinelRef} style={{ height: '1px', width: '100%' }} />
          )}

          {loadingMore && tabState[type] && tabState[type].hasMoreData && (
            <div className="loading-more">
              <div className="loading-more-spinner">
                <Loader type="Oval" color="#F334BE" height={40} width={40} />
              </div>
            </div>
          )}
        </section>
      </main>

      <ModernFootermenu
        onUploadSuccess={refreshContent}
      />

      <PPVFollowModal
        modalOpen={isFollowModalOpen}
        toggleModal={toggleFollowModal}
        creatorInfo={creatorInfo}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(ViewCreatorProfile);
